import { EmptyState, FlexBox } from '@dixa/lib-shared-habla';
import { createColumnHelper, useTable } from '@tanstack/react-table';
import { UseArticlesQueryParams } from 'collection/pages/articles/Articles';
import { conditionalArticlesSort } from 'collection/pages/articles/components/articlesTable';
import { useArticlesFilters } from 'collection/pages/articles/hooks/useArticlesFilters';
import { useAtomValue } from 'jotai';
import {
  ArticleListItemResultFragment,
  useArticlesQuery,
} from 'lib-knowledge-management-common/graphql';
import { isValidSearchLength } from 'lib-knowledge-utils/string';
import { collectionIdAtom } from 'models/collectionStore';
import { useMemo, useRef } from 'react';

import { ArticlesTable } from 'components/articlesTable';
import { getArticleTitleColumn } from 'components/articlesTable/columns';
import {
  articlesTableFeatures,
  ArticlesTableFeatures,
} from 'components/articlesTable/features';
import { Article } from 'components/articlesTable/types';

import { getCollectionSearchActionColumn } from './cells';

/** Component for rendering article search results in collection manager */
export function CollectionArticlesTable(): JSX.Element {
  const { store, articlesQueryVariables } = useArticlesFilters();
  const searchTerm = store.search || '';
  const isValidSearch = isValidSearchLength(searchTerm);
  const collectionId = useAtomValue(collectionIdAtom);
  const articlesQueryParams: UseArticlesQueryParams = {
    variables: {
      ...articlesQueryVariables,
      filters: collectionId
        ? {
            ...articlesQueryVariables.filters,
            collectionId: [collectionId],
          }
        : undefined,
    },
    fetchPolicy: 'cache-and-network',
    skip: !isValidSearch,
  };

  const {
    data: articlesData,
    error: articlesError,
    networkStatus,
    loading,
    fetchMore,
  } = useArticlesQuery(articlesQueryParams);

  const articles: ArticleListItemResultFragment[] = useMemo(() => {
    return conditionalArticlesSort({ search: searchTerm, articlesData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articlesData, searchTerm, articlesQueryVariables]); // we want this to run if articlesQueryVariables updates

  const columnHelper = createColumnHelper<ArticlesTableFeatures, Article>();
  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor('title', getArticleTitleColumn()),
        columnHelper.accessor('id', getCollectionSearchActionColumn()),
      ]),
    [columnHelper],
  );
  const table = useTable({
    features: articlesTableFeatures,
    columns,
    data: articles,
  });
  const lastRowRef = useRef<HTMLTableRowElement>(null);

  if (articlesError) {
    return (
      <FlexBox
        height="100%"
        justifyContent="flex-start"
        alignItems="center"
        flexDirection="column"
        padding="400"
      >
        <EmptyState
          icon="file-text"
          title="Error searching for articles"
          description="Please try again"
        />
      </FlexBox>
    );
  }

  return (
    <ArticlesTable
      fetchMore={fetchMore}
      articlesQuery={articlesData?.articles.query}
      loading={loading}
      networkStatus={networkStatus}
      lastRowRef={lastRowRef}
    >
      <ArticlesTable.Body
        table={table}
        lastRowRef={lastRowRef}
        networkStatus={networkStatus}
      />
    </ArticlesTable>
  );
}
